import React, {FC, useEffect, useState} from "react"
import {Row} from "react-bootstrap"
import ReactDatePicker from "react-datepicker"
import momentTZ from "moment-timezone"
import moment from "moment"

import Flex from "@irati/ui/src/Flex"
import Tabs from "@irati/ui/src/Tabs"
import {Body, H6} from "@irati/ui/src/Typography"
import UserSearch from "@irati/ui/src/UserSearch"
import {useTheme} from "@irati/ui/src/global/useTheme"
import {Input, Select} from "@irati/ui/src/FormItem"
import {ButtonRouterLink} from "@irati/ui/src/Link"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import FormattedMessage, {useIntl} from "@irati/utils/lang"
import Expander, {ExpanderArrow} from "@irati/ui/src/Expander"
import {OverlayPlacement} from "@irati/ui/src/Tooltip"
import {useListOrdersQuery, useOrderFiltersQuery, useExportOrdersQuery} from "@irati/payrollpay/hooks"
import Loading from "@irati/ui/src/Loading"
import Alert, {AlertTypes} from "@irati/ui/src/Alert"
import Button from "@irati/ui/src/Button"
import adminTracking from "@irati-tracking/admin"

import ProfilePicAndName from "../../components/OrderManagement/ProfilePicAndName"
import StatusPill from "../../components/OrderManagement/StatusPill"
import {ORDER_MANAGEMENT_BULK_UPDATE} from "../../routes/routeConfig"
import OrderManagementView from "./View"
import {StyledReactTable, FlexChild, StyledIconTooltip, StyledFilterChipToggle, StyledForm} from "./styled"

interface Props {}

const OrderManagement: FC<Props> = () => {
  const getStartDate = (timezone: string) => {
    const now = momentTZ().tz(timezone)
    now.seconds(0)
    now.milliseconds(0)
    if (now.minute() <= 30) return now.set({minute: 30})
    if (now.minute() > 30) return now.set({hour: now.hours() + 1, minute: 0})
    return now
  }

  const [first_name, setFirstName] = useState(undefined)
  const [last_name, setLastName] = useState(undefined)
  const [approval_status, setApprovalStatus] = useState("All")
  const [order_status, setOrderStatus] = useState("All")
  const [employeeNumber, setEmployeeNumber] = useState("")
  const [NINumber, setNINumber] = useState("")
  const [payrollPeriod, setPayrollPeriod] = useState(undefined)
  const [orderDate, setOrderDate] = useState(undefined)
  const [filterToggle, setFilterToggle] = useState(false)
  const [employeeNumbers, setEmployeeNumbers] = useState([])
  const [NINumbers, setNINumbers] = useState([])
  const [payrollPeriods, setPayrollPeriods] = useState([])
  const [approvalStatuses, setApprovalStatuses] = useState([])
  const [orderStatuses, setOrderStatuses] = useState([])
  const [orders_date_from, setOrdersDateFrom] = useState(undefined)
  const [orders_date_to, setOrdersDateTo] = useState(undefined)
  const [benefit_type, setBenefitType] = useState(undefined)
  const [benefitTypes, setBenefitTypes] = useState(undefined)
  const [showDownloadOrderListAlert, setShowDownloadOrderListAlert] = useState(false)
  const [isDateExpanded, setIsDateExpanded] = useState(false)
  const [timezone, setTimezone] = useState(momentTZ.tz.guess())
  const [orderId, setOrderId] = useState(undefined)
  const [page, setPage] = useState(1)
  const [totalOrders, setTotalOrders] = useState(0)
  const [orders, setOrders] = useState([])
  const {formatMessage} = useIntl()

  const whiteListedBenefits = [
    formatMessage({id: "admin.components.orderManagement.list.whitelistedBenefits.all"}),
    formatMessage({id: "admin.components.orderManagement.list.whitelistedBenefits.1"}),
    formatMessage({id: "admin.components.orderManagement.list.whitelistedBenefits.2"}),
    formatMessage({id: "admin.components.orderManagement.list.whitelistedBenefits.3"}),
    formatMessage({id: "admin.components.orderManagement.list.whitelistedBenefits.4"}),
    formatMessage({id: "admin.components.orderManagement.list.whitelistedBenefits.5"}),
    formatMessage({id: "admin.components.orderManagement.list.whitelistedBenefits.6"})
  ]

  const [listOrders, {data, load}] = useListOrdersQuery({
    first_name,
    last_name,
    organisation_approval: approval_status !== "All" ? approval_status : undefined,
    order_status: order_status !== "All" ? order_status : undefined,
    employee_number: employeeNumber,
    ni_number: NINumber,
    pay_frequency: payrollPeriod,
    payroll_period: orderDate,
    orders_date_from,
    orders_date_to,
    benefit_type,
    page
  })
  const [orderFilters, orderFiltersResponse] = useOrderFiltersQuery()
  const [exportOrders, exportOrdersResponse] = useExportOrdersQuery({
    first_name,
    last_name,
    organisation_approval: approval_status !== "All" ? approval_status : undefined,
    order_status: order_status !== "All" ? order_status : undefined,
    employee_number: employeeNumber,
    ni_number: NINumber,
    pay_frequency: payrollPeriod,
    payroll_period: orderDate,
    benefit_type,
    page
  })
  const theme = useTheme()

  useEffect(() => {
    setPage(1)
    setOrders([])
  }, [first_name, last_name, approval_status, employeeNumber, NINumber, payrollPeriod, orders_date_from, orders_date_to, benefit_type])

  useEffect(() => {
    listOrders()
    orderFilters()
    adminTracking.orderManagementList()
  }, [])

  useEffect(() => {
    if (data) {
      if (data?.orders) {
        if (page === 1) {
          setOrders(data?.orders)
          setTotalOrders(data?.orders?.length)
        } else {
          setOrders([...orders, ...data?.orders])
        }
      }
      const whitelistedBenefitTypes = data?.scheme_types_by_count.filter(({name}) => whiteListedBenefits.includes(name))
      if (!benefitTypes) {
        setBenefitTypes(whitelistedBenefitTypes)
      }
      setTotalOrders(whitelistedBenefitTypes?.find((tab) => tab.name.includes(benefit_type || "All"))?.value)
    }
  }, [data])

  useEffect(() => {
    if (orderFiltersResponse?.data) {
      const {employee_numbers, ni_numbers, payroll_periods_by_frequency, status, organisation_approval} = orderFiltersResponse?.data
      setApprovalStatuses(organisation_approval)
      setOrderStatuses(status)
      setEmployeeNumbers(employee_numbers.map((eNumber) => ({label: eNumber, value: eNumber})))
      setNINumbers(ni_numbers.map((niNumber) => ({label: niNumber, value: niNumber})))
      setPayrollPeriods(payroll_periods_by_frequency)
      if (payroll_periods_by_frequency?.length === 1) {
        setPayrollPeriod(undefined)
      }
    }
  }, [orderFiltersResponse?.data])

  const handleUserUpdate = (_uuid, user) => {
    setFirstName(user?.firstName)
    setLastName(user?.lastName)
    handleFilterUpdate()
  }

  const handlePayrollPeriodUpdate = (value) => {
    setPayrollPeriod(value)
    setOrderDate(null)
    handleFilterUpdate()
  }

  const handleApprovalStatusUpdate = (value) => {
    setApprovalStatus(value)
    setPayrollPeriod(null)
    setOrderDate(null)
    handleFilterUpdate()
  }

  const handleDownloadOrderList = () => {
    exportOrders()
    setShowDownloadOrderListAlert(true)
    adminTracking.orderManagementDownload()
  }

  const minDate = () => {
    return getStartDate(timezone).clone()
  }

  const handleClearDates = () => {
    setOrdersDateFrom(undefined)
    setOrdersDateTo(undefined)
  }

  const handleShowMore = () => {
    setPage(page + 1)
  }

  const handleBenefitType = (updatedBenefit: string) => {
    setBenefitType(updatedBenefit)
    adminTracking.orderManagementBenefitClick(updatedBenefit)
  }

  const handleOrderClick = (updatedOrderId: string) => {
    setOrderId(updatedOrderId)
    adminTracking.orderManagementView(updatedOrderId)
  }

  const handleBack = () => {
    setOrderId(undefined)
    adminTracking.orderManagementBack()
  }

  const handleOrderStatusUpdate = (updatedStatus: string) => {
    setOrderStatus(updatedStatus)
    handleFilterUpdate()
  }

  const handleEmployeeNumberUpdate = (updatedEmployeeNumber: string) => {
    setEmployeeNumber(updatedEmployeeNumber)
    handleFilterUpdate()
  }

  const handleNINumberUpdate = (updatedNINumber: string) => {
    setNINumber(updatedNINumber)
    handleFilterUpdate()
  }

  const handleFilterUpdate = () => {
    const filters = {first_name, last_name, approval_status, order_status, employeeNumber, NINumber, payrollPeriod, orderDate}
    adminTracking.orderManagementFilter(filters)
  }

  if (orderId) {
    return <OrderManagementView order_number={orderId?.toString()} onBack={handleBack} orders={orders} />
  }

  return (
    <>
      {showDownloadOrderListAlert && (
        <Alert
          type={AlertTypes.success}
          title="admin.components.orderManagement.list.alertTitle"
          onHide={() => setShowDownloadOrderListAlert(false)}
        />
      )}
      {benefitTypes?.length && (
        <Tabs
          mountOnEnter
          unmountOnExit
          onSelect={handleBenefitType}
          activeKey={benefit_type}
          tabs={benefitTypes
            .filter((tab) => {
              const key = tab.name.substring(tab.name.indexOf("(") + 1, tab.name.lastIndexOf(")"))
              return tab.name === "All" || key?.length
            })
            .map((tab) => {
              const title = tab.name === "All" ? "All" : tab.name.substring(0, tab.name.indexOf("("))
              const key = tab.name.substring(tab.name.indexOf("(") + 1, tab.name.lastIndexOf(")"))
              return {
                title,
                key,
                pillText: tab.value || undefined
              }
            })}
        />
      )}
      <Flex justifyContent="space-between" marginTop={2} marginBottom={2} gap={2}>
        <FlexChild>
          <Row>
            <UserSearch
              id="user-search-order-management"
              label="admin.components.orderManagement.list.label.searchPeople"
              onChange={handleUserUpdate}
              isClearable
            />
          </Row>
        </FlexChild>
        <FlexChild>
          <Row>
            <Select
              label="Approval Status"
              options={approvalStatuses.map((status) => ({label: status, value: status}))}
              value={approval_status}
              onChange={handleApprovalStatusUpdate}
              components={{
                SingleValue: () => <StatusPill status={approval_status} />
              }}
            />
          </Row>
        </FlexChild>
        <FlexChild>
          <Row>
            <Select
              label="admin.components.orderManagement.list.label.orderStatus"
              options={orderStatuses.map((status) => ({label: status, value: status}))}
              value={order_status}
              onChange={handleOrderStatusUpdate}
              components={{
                SingleValue: () => <StatusPill status={order_status} />
              }}
            />
          </Row>
        </FlexChild>
        <StyledFilterChipToggle
          id="orderManagement_filters_button"
          icon={IconTypes.filter3}
          isToggled={filterToggle}
          onClick={() => setFilterToggle(!filterToggle)}
        />
      </Flex>
      {filterToggle && (
        <div>
          <Flex justifyContent="space-between" marginTop={2} marginBottom={2} gap={2}>
            <FlexChild>
              <Row>
                <Select
                  isClearable
                  label="admin.components.orderManagement.list.label.employeeNr"
                  options={employeeNumbers}
                  onChange={handleEmployeeNumberUpdate}
                  value={employeeNumber}
                />
              </Row>
            </FlexChild>
            <FlexChild>
              <Row>
                <Select
                  isClearable
                  label="admin.components.orderManagement.list.label.niNr"
                  options={NINumbers}
                  onChange={handleNINumberUpdate}
                  value={NINumber}
                />
              </Row>
            </FlexChild>
            {payrollPeriods.length > 1 && (
              <FlexChild>
                <Row>
                  <Select
                    isClearable
                    isDisabled={approval_status !== "Accepted"}
                    label="admin.components.orderManagement.list.label.payroll"
                    options={payrollPeriods.map((period) => ({label: period.frequency, value: period.frequency}))}
                    onChange={handlePayrollPeriodUpdate}
                    value={payrollPeriod}
                  />
                  <StyledIconTooltip placement={OverlayPlacement.Bottom}>
                    <FormattedMessage id="admin.components.orderManagement.list.iconTooltip" />
                  </StyledIconTooltip>
                </Row>
              </FlexChild>
            )}
          </Flex>
          <Flex justifyContent="space-between" marginTop={2} marginBottom={2} gap={2}>
            <FlexChild>
              <Row>
                <Select
                  isDisabled={approval_status !== "Accepted" || (!payrollPeriod && payrollPeriods.length > 1)}
                  isClearable
                  label="admin.components.orderManagement.list.label.payrollPeriod"
                  options={payrollPeriods
                    .find((period) => period.frequency === payrollPeriod)
                    ?.periods.map((period) => ({label: period, value: period}))}
                  onChange={setOrderDate}
                  value={orderDate}
                />
                <StyledIconTooltip>
                  <FormattedMessage id="admin.components.orderManagement.list.iconTooltip" />
                </StyledIconTooltip>
              </Row>
            </FlexChild>
            <FlexChild>
              <Expander
                HeaderLabel={() => (
                  <StyledForm>
                    <Input
                      name="scheduledAt"
                      label="admin.components.orderManagement.list.label.orderDate"
                      flush
                      addon={{
                        after: <ExpanderArrow isExpanded={isDateExpanded} size={12} color={theme.colors.greys.mediumLight} />
                      }}
                      value={
                        orders_date_from || orders_date_to
                          ? `${moment(orders_date_from)?.format("D MM YYYY")} - ${moment(orders_date_to)?.format("D MM YYYY")}`
                          : formatMessage({id: "admin.components.orderManagement.list.label.orderDateValue"})
                      }
                      placeholder="recognition.create.dateTime.label"
                    />
                  </StyledForm>
                )}
                showArrow={false}
                absolute
                fullWidth
                clickOut
                onChange={setIsDateExpanded}
                showText="text"
                iconSize={12}>
                <Flex alignItems="flex-start">
                  <ReactDatePicker
                    inline
                    calendarStartDay={1}
                    minDate={minDate().clone().subtract(10, "years").toDate()}
                    maxDate={minDate().toDate()}
                    onChange={setOrdersDateFrom}
                  />
                  <ReactDatePicker
                    inline
                    calendarStartDay={1}
                    minDate={minDate().clone().subtract(10, "years").toDate()}
                    maxDate={minDate().toDate()}
                    onChange={setOrdersDateTo}
                  />
                  <Button onClick={handleClearDates}>
                    <FormattedMessage id="admin.components.orderManagement.list.clearDates" />
                  </Button>
                </Flex>
              </Expander>
            </FlexChild>
          </Flex>
        </div>
      )}
      <Loading load={load}>
        <Flex justifyContent="space-between" marginBottom={2}>
          <Body small color={theme.colors.greys.grey50}>
            <FormattedMessage
              id="admin.components.orderManagement.list.viewingOrders"
              values={{orders: orders?.length, total: totalOrders}}
            />
          </Body>
          <div>
            <Button onClick={handleDownloadOrderList} tertiary marginRight>
              <Flex marginTop>
                <Icon icon={IconTypes.downloadReport} marginRight />
                <FormattedMessage id="admin.components.orderManagement.list.downloadCSV" />
              </Flex>
            </Button>
            <ButtonRouterLink tertiary to={ORDER_MANAGEMENT_BULK_UPDATE} onClick={() => adminTracking.orderManagementImportClick()}>
              <Flex marginTop>
                <Icon icon={IconTypes.downloadReport} marginRight />
                <FormattedMessage id="admin.components.orderManagement.list.bulkOrders" />
              </Flex>
            </ButtonRouterLink>
          </div>
        </Flex>
        <StyledReactTable
          data={orders}
          manual={false}
          showPagination={false}
          columns={[
            {
              Header: (
                <H6 color={theme.colors.greys.grey50}>
                  <FormattedMessage id="admin.components.orderManagement.list.header.name" />
                </H6>
              ),
              id: "name",
              accessor: (row) => (
                <ProfilePicAndName
                  firstName={row.first_name}
                  lastName={row.surname}
                  employeeNumber={`${row.employee_id} - ${row.ni_number}`}
                />
              ),
              width: 250
            },
            {
              Header: (
                <H6 color={theme.colors.greys.grey50}>
                  <FormattedMessage id="admin.components.orderManagement.list.header.order" />
                </H6>
              ),
              id: "orderNumber",
              accessor: (row) => row.order_number,
              width: 130
            },
            {
              Header: (
                <H6 color={theme.colors.greys.grey50}>
                  <FormattedMessage id="admin.components.orderManagement.list.header.benefit" />
                </H6>
              ),
              id: "benefitType",
              accessor: (row) => row.benefit_type,
              width: 200
            },
            {
              Header: (
                <H6 color={theme.colors.greys.grey50}>
                  <FormattedMessage id="admin.components.orderManagement.list.header.approval" />
                </H6>
              ),
              id: "approvalStatus",
              accessor: (row) => <StatusPill status={row.approval_status} />,
              width: 140
            },
            {
              Header: (
                <H6 color={theme.colors.greys.grey50}>
                  <FormattedMessage id="admin.components.orderManagement.list.header.orderStatus" />
                </H6>
              ),
              id: "orderStatus",
              accessor: (row) => <StatusPill status={row.order_status} />,
              width: 200
            },
            {
              Header: (
                <H6 color={theme.colors.greys.grey50}>
                  <FormattedMessage id="admin.components.orderManagement.list.header.orderDate" />
                </H6>
              ),
              id: "orderDate",
              accessor: (row) => moment(row.effective_date).format("DD MMM YYYY"),
              width: 130
            },
            {
              Header: "",
              id: "details",
              accessor: (row) => (
                <Button small tertiary onClick={() => handleOrderClick(row.order_number)}>
                  <Icon icon={IconTypes.document} marginBottom={-0.5} marginRight={1} />
                  <FormattedMessage id="admin.components.orderManagement.list.details" />
                </Button>
              )
            }
          ]}
        />
        {!data ||
          (orders?.length === 0 && (
            <Body small>
              <FormattedMessage id="admin.components.orderManagement.list.noOrders" />
            </Body>
          ))}
        {totalOrders > orders?.length && (
          <Flex justifyContent="center">
            <Button onClick={handleShowMore}>
              <FormattedMessage id="admin.components.orderManagement.list.showMore" />
            </Button>
          </Flex>
        )}
      </Loading>
    </>
  )
}

export default OrderManagement
